import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';

const FilterPanel = ({ filters, onFilterChange, onClearFilters, isOpen, onClose }) => {
  const [localFilters, setLocalFilters] = useState(filters);

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'mumbai', label: 'Mumbai' },
    { value: 'delhi', label: 'Delhi NCR' },
    { value: 'bangalore', label: 'Bangalore' },
    { value: 'hyderabad', label: 'Hyderabad' },
    { value: 'pune', label: 'Pune' },
    { value: 'chennai', label: 'Chennai' },
    { value: 'kolkata', label: 'Kolkata' },
    { value: 'ahmedabad', label: 'Ahmedabad' },
    { value: 'jaipur', label: 'Jaipur' },
    { value: 'surat', label: 'Surat' }
  ];

  const experienceOptions = [
    { value: 'all', label: 'All Experience Levels' },
    { value: 'fresher', label: 'Fresher (0-1 years)' },
    { value: 'entry', label: 'Entry Level (1-3 years)' },
    { value: 'mid', label: 'Mid Level (3-6 years)' },
    { value: 'senior', label: 'Senior Level (6-10 years)' },
    { value: 'lead', label: 'Lead (10+ years)' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'it', label: 'IT & Software' },
    { value: 'sales', label: 'Sales & Marketing' },
    { value: 'finance', label: 'Finance & Accounting' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'education', label: 'Education & Training' },
    { value: 'design', label: 'Design & Creative' },
    { value: 'engineering', label: 'Engineering' },
    { value: 'consulting', label: 'Consulting' }
  ];

  const companySizeOptions = [
    { value: 'all', label: 'All Company Sizes' },
    { value: 'startup', label: 'Startup (1-50)' },
    { value: 'small', label: 'Small (51-200)' },
    { value: 'medium', label: 'Medium (201-1000)' },
    { value: 'large', label: 'Large (1000+)' }
  ];

  const handleLocalFilterChange = (key, value) => {
    const updatedFilters = { ...localFilters, [key]: value };
    setLocalFilters(updatedFilters);
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
    if (onClose) onClose();
  };

  const handleClearAll = () => {
    const clearedFilters = {
      keyword: '',
      location: 'all',
      experience: 'all',
      category: 'all',
      jobTypes: [],
      salaryMin: 0,
      salaryMax: 50,
      isRemote: false,
      companySize: 'all'
    };
    setLocalFilters(clearedFilters);
    onClearFilters();
    if (onClose) onClose();
  };

  const formatSalary = (value) => {
    if (value >= 10) return `${value}L`;
    return `${value}L`;
  };

  const content = (
    <div className="space-y-6">
      <div>
        <Input
          type="search"
          label="Search Keywords"
          placeholder="Job title, skills, or company"
          value={localFilters?.keyword}
          onChange={(e) => handleLocalFilterChange('keyword', e?.target?.value)}
        />
      </div>

      <div>
        <Select
          label="Location"
          options={locationOptions}
          value={localFilters?.location}
          onChange={(value) => handleLocalFilterChange('location', value)}
          searchable
        />
      </div>

      <div>
        <Select
          label="Experience Level"
          options={experienceOptions}
          value={localFilters?.experience}
          onChange={(value) => handleLocalFilterChange('experience', value)}
        />
      </div>

      <div>
        <Select
          label="Category"
          options={categoryOptions}
          value={localFilters?.category}
          onChange={(value) => handleLocalFilterChange('category', value)}
          searchable
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-3">Job Type</label>
        <div className="space-y-2">
          {['Full-time', 'Part-time', 'Contract', 'Internship']?.map((type) => (
            <Checkbox
              key={type}
              label={type}
              checked={localFilters?.jobTypes?.includes(type)}
              onChange={(e) => {
                const updated = e?.target?.checked
                  ? [...localFilters?.jobTypes, type]
                  : localFilters?.jobTypes?.filter(t => t !== type);
                handleLocalFilterChange('jobTypes', updated);
              }}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-3">
          Salary Range (LPA)
        </label>
        <div className="space-y-3">
          <input
            type="range"
            min="0"
            max="50"
            step="1"
            value={localFilters?.salaryMax}
            onChange={(e) => handleLocalFilterChange('salaryMax', parseInt(e?.target?.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">₹0L</span>
            <span className="font-medium text-primary">
              Up to ₹{formatSalary(localFilters?.salaryMax)}
            </span>
            <span className="text-muted-foreground">₹50L+</span>
          </div>
        </div>
      </div>

      <div>
        <Checkbox
          label="Remote Jobs Only"
          checked={localFilters?.isRemote}
          onChange={(e) => handleLocalFilterChange('isRemote', e?.target?.checked)}
        />
      </div>

      <div>
        <Select
          label="Company Size"
          options={companySizeOptions}
          value={localFilters?.companySize}
          onChange={(value) => handleLocalFilterChange('companySize', value)}
        />
      </div>

      <div className="flex gap-3 pt-4 border-t border-border">
        <Button 
          variant="outline" 
          fullWidth
          onClick={handleClearAll}
        >
          Clear All
        </Button>
        <Button 
          variant="default" 
          fullWidth
          onClick={handleApplyFilters}
        >
          Apply Filters
        </Button>
      </div>
    </div>
  );

  if (isOpen !== undefined) {
    return (
      <>
        {isOpen && (
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1100] lg:hidden"
            onClick={onClose}
          />
        )}
        <div className={`fixed top-0 right-0 bottom-0 w-full max-w-sm bg-card border-l border-border z-[1200] overflow-y-auto transform transition-transform duration-300 lg:hidden ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
          <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
            <h2 className="text-lg font-semibold text-foreground">Filters</h2>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-muted transition-smooth"
              aria-label="Close filters"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
          <div className="p-4">
            {content}
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Filters</h2>
        <button
          onClick={handleClearAll}
          className="text-sm text-primary hover:text-primary/80 transition-smooth"
        >
          Clear All
        </button>
      </div>
      {content}
    </div>
  );
};

export default FilterPanel;